import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogPortal, DialogOverlay } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "agent_david_welcome_shown";

/**
 * One-time welcome modal after first verified login.
 * No X, no backdrop dismiss, no Escape — only [Let's go →].
 * Controlled via `open` from parent (e.g. App.jsx).
 */
export default function WelcomeModal({ open, onComplete }) {
  if (!open) {
    return null;
  }

  const handleLetsGo = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      /* ignore */
    }
    onComplete?.();
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogPortal>
        <DialogOverlay className="pointer-events-auto" />
        <DialogPrimitive.Content
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          className={cn("fixed left-[50%] top-[50%] z-[60] w-full max-w-[520px] translate-x-[-50%] translate-y-[-50%]",
            "rounded-lg border border-border bg-card p-6 shadow-lg",
            "text-foreground"
          )}
        >
          <div className="text-center space-y-2">
            <h2 className="text-xl font-semibold tracking-tight">
              Welcome to Agent David 🎉
            </h2>
            <p className="text-sm text-muted-foreground">
              AI-powered marketing intelligence for agencies and marketing teams
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2 mt-6 text-center">
            {[
              { n: "1", t: "About you", s: "1 min" },
              { n: "2", t: "Create workspace", s: "2 mins" },
              { n: "3", t: "Connect account", s: "2 mins" },
            ].map((c) => (
              <div
                key={c.n}
                className="rounded-lg border border-border bg-muted/40 px-2 py-3 flex flex-col gap-1"
              >
                <span className="text-xs font-medium text-muted-foreground">{c.n}</span>
                <span className="text-xs font-semibold leading-tight">{c.t}</span>
                <span className="text-[10px] text-muted-foreground">{c.s}</span>
              </div>
            ))}
          </div>

          <p className="text-sm text-muted-foreground text-center mt-6">
            Takes about 5 minutes. Complete this setup to access the full app.
          </p>

          <div className="mt-6 flex justify-center">
            <Button type="button" className="min-w-[160px]" onClick={handleLetsGo}>
              Let&apos;s go →
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
